import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {DialogFooter} from '@common/ui/overlays/dialog/dialog-footer';
import {Button} from '@common/ui/buttons/button';
import {Trans} from '@common/i18n/trans';
import {useState} from 'react';
import clsx from 'clsx';
import {QrPresets} from '@app/dashboard/links/sharing/qr-code-presets';
import {useDialogContext} from '@common/ui/overlays/dialog/dialog-context';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {ImageSelector} from '@common/ui/images/image-selector';

interface QrCodeDialogProps {
  url: string;
}
export function QrCodeDialog({url}: QrCodeDialogProps) {
  const [preset, setPreset] = useState(QrPresets[0]);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const {close} = useDialogContext();
  const qrSrc = `${url}/qr?fg=${preset.fg}&bg=${preset.bg}${
    logo ? `&logo=${encodeURIComponent(logo)}` : ''
  }`;

  return (
    <Dialog size="sm">
      <DialogHeader>
        <Trans message="QR Code" />
      </DialogHeader>
      <DialogBody>
        <div
          className="mx-auto mb-24 flex h-180 w-180 items-center justify-center rounded border p-10"
          style={{backgroundColor: `#${preset.bg}`}}
        >
          <img
            src={qrSrc}
            alt="QR code"
            className="h-full w-full"
            key={qrSrc}
          />
        </div>
        <div className="mb-10 text-sm font-semibold">
          <Trans message="Style" />
        </div>
        <div className="mb-24 grid grid-cols-5 gap-10">
          {QrPresets.map(p => (
            <button
              key={p.id}
              type="button"
              title={p.label.message}
              className={clsx(
                'flex h-32 w-32 items-center justify-center rounded-full border-2 p-2',
                preset.id === p.id ? 'border-primary' : 'border-transparent',
              )}
              style={{backgroundColor: `#${p.bg}`}}
              onClick={() => setPreset(p)}
            >
              <span
                className="block h-full w-full rounded-full"
                style={{backgroundColor: `#${p.fg}`}}
              />
            </button>
          ))}
        </div>
        <div className="mb-10 text-sm font-semibold">
          <Trans message="Logo" />
        </div>
        <div className="mb-6 text-xs text-muted">
          <Trans message="Add your own photo or logo in the center to make this QR code uniquely yours." />
        </div>
        <FileUploadProvider>
          <ImageSelector
            value={logo}
            onChange={setLogo}
            diskPrefix="widgets"
            variant="square"
            previewSize="w-70 h-70"
            previewRadius="rounded"
            showRemoveButton
          />
        </FileUploadProvider>
      </DialogBody>
      <DialogFooter dividerTop>
        <Button onClick={() => close()}>
          <Trans message="Close" />
        </Button>
        <Button
          variant="flat"
          color="primary"
          href={qrSrc}
          download="qr-code.svg"
          target="_blank"
          rel="noreferrer"
        >
          <Trans message="Download" />
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
